import { useEffect, useState } from 'react';

import { Card, Text } from '@gympass/yoga';
import { useQuery } from '@tanstack/react-query';
import withAuth from 'hocs/withAuth/withAuth';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useLogger } from 'hooks/useLogger/useLogger';
import {
  AssessmentsResult,
  ClinicalAssessment,
  getEndUserInfo,
  ScoreType,
  getEndUserAssessmentsV3,
} from 'services';

import {
  ClinicalAssessmentTable,
  TabList,
  Loading,
  HeaderUserInfo,
} from 'components';

import * as S from './styles';

const ClinicalAssessmets: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logger = useLogger();
  const { id = '' } = useParams<{
    id: string;
  }>();

  const initialAssessments: Record<ScoreType, ClinicalAssessment[]> = {
    gad: [],
    phq: [],
    who: [],
  };
  const [assessments, setAssessments] =
    useState<Record<ScoreType, ClinicalAssessment[]>>(initialAssessments);

  const {
    data: userInfo,
    isLoading: isLoadingEndUserInfo,
    refetch: userInfoRefetch,
  } = useQuery(['endUserInfo', id], () => getEndUserInfo(id));

  const { refetch: refetchAssessments } = useQuery(
    ['clinicalEndUserAssessments', id],
    () => getEndUserAssessmentsV3(id),
    {
      enabled: false,
      cacheTime: 0,
      onSuccess: (data: AssessmentsResult[]) => {
        const gad = data.find(assessment => assessment?.type === ScoreType.GAD);
        const phq = data.find(assessment => assessment?.type === ScoreType.PHQ);
        const who = data.find(assessment => assessment?.type === ScoreType.WHO);

        const assessmentsResult: Record<ScoreType, ClinicalAssessment[]> = {
          gad: gad?.data ?? [],
          phq: phq?.data ?? [],
          who: who?.data ?? [],
        };

        setAssessments(assessmentsResult);
      },
      onError: e => {
        if (typeof e === 'string') {
          logger.error(e);
        }
      },
    },
  );

  useEffect(() => {
    userInfoRefetch();
    refetchAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.data]);

  return (
    <S.UserInfoContainer>
      <Loading isLoading={isLoadingEndUserInfo} />
      <HeaderUserInfo user={userInfo?.data} />
      <S.Divisor />
      <S.TabsContainer data-testid="tabClinicalTabs">
        <TabList selectedIndex={3} />
        <S.SessionsSection>
          <Card.Header>
            <Text.H5 data-testid="gadTitle">
              <FormattedMessage id="ENDUSER.CLINICALASSESSMENT.GAD.LABEL" />
            </Text.H5>
          </Card.Header>
          <S.SessionsContent data-testid="gadAssessmentTable">
            {assessments.gad?.length > 0 && (
              <ClinicalAssessmentTable
                data={assessments.gad}
                onClickAnswers={(submissionId, date) =>
                  navigate(`${pathname}/${submissionId}`, {
                    state: {
                      submissionId,
                      date,
                      scoreType: ScoreType.GAD,
                    },
                  })
                }
              />
            )}
          </S.SessionsContent>
        </S.SessionsSection>
        <S.SessionsSection>
          <Card.Header>
            <Text.H5 data-testid="phqTitle">
              <FormattedMessage id="ENDUSER.CLINICALASSESSMENT.PHQ.LABEL" />
            </Text.H5>
          </Card.Header>
          <S.SessionsContent data-testid="phqAssessmentTable">
            {assessments.phq?.length > 0 && (
              <ClinicalAssessmentTable
                data={assessments.phq}
                onClickAnswers={(submissionId, date) =>
                  navigate(`${pathname}/${submissionId}`, {
                    state: {
                      date,
                      scoreType: ScoreType.PHQ,
                    },
                  })
                }
              />
            )}
          </S.SessionsContent>
        </S.SessionsSection>
        <S.SessionsSection>
          <Card.Header>
            <Text.H5 data-testid="whoTitle">
              <FormattedMessage id="ENDUSER.CLINICALASSESSMENT.WHO.LABEL" />
            </Text.H5>
          </Card.Header>
          {assessments.who?.length > 0 && (
            <S.SessionsContent data-testid="whoAssessmentTable">
              <ClinicalAssessmentTable
                data={assessments.who}
                onClickAnswers={(submissionId, date) =>
                  navigate(`${pathname}/${submissionId}`, {
                    state: {
                      date,
                      scoreType: ScoreType.WHO,
                    },
                  })
                }
              />
            </S.SessionsContent>
          )}
        </S.SessionsSection>
      </S.TabsContainer>
    </S.UserInfoContainer>
  );
};

export default withAuth(ClinicalAssessmets);
